import { SlideFrame } from '../SlideFrame';

// "What this means for you" — the payoff slide. Ports the website home
// page section. Sits after the Model Rating evidence as the summary
// moment: "we have audited the model; here is what it delivers in
// practice." Sets up the live Portfolio + Alpha-Zone slides that
// demonstrate the claims.
// `universe` interpolated into the Broader-coverage description so the
// "1,100 companies" number stays in sync with the live visualizer count.
function buildBenefits(universe: number) {
  return [
  {
    num: '01',
    title: 'Broader coverage',
    description: `${universe.toLocaleString()} companies across the US, Canada, Hong Kong, and Europe. Every one of them, every day. More shots on goal, more mispriced companies found.`,
  },
  {
    num: '02',
    title: 'More consistent',
    description: 'Every company is scored the same way, on the same 54 factors. No analyst bias. No rules that quietly bend for one we already like.',
  },
  {
    num: '03',
    title: 'Faster reaction',
    description: 'Earnings, insider activity, estimate revisions, and analyst rating changes are surfaced within minutes of the event. Quality moves. We track it.',
  },
  {
    num: '04',
    title: 'Idea generation',
    description: 'An AI analyst that never sleeps. Reads the universe overnight, considers portfolio fit and correlation, surfaces one or two high-conviction ideas by morning.',
  },
  ];
}

export function SlideWhatItMeans({ universe }: { universe: number }) {
  const BENEFITS = buildBenefits(universe);
  return (
    <SlideFrame pageNum={17} title="SUMMARY - WHAT THE VISUALIZER MEANS FOR YOU" titleOffsetPx={38}
      banner="Traditional asset management got us this far. AI brings us to the future. The Visualizer is how we get there.">
      <div className="h-full flex flex-col gap-4 px-10 py-2">
        {/* 4 benefit cards — content-sized, no flex-1. Steve directive
            2026-05-15: "i feel the boxes are too long" — the cards were
            stretching to fill the slide; now they're auto-height. */}
        <div className="grid grid-cols-4 gap-3">
          {BENEFITS.map(b => (
            <div key={b.num} className="bg-[#1e293b] rounded-2xl border border-[#c9a84c]/30 px-4 py-3 flex flex-col">
              <div className="text-[#c9a84c] text-[12px] font-bold tracking-widest tabular-nums mb-2">{b.num}</div>
              <div className="text-white text-[18px] font-bold leading-tight mb-2">{b.title}</div>
              <div className="text-[#cbd5e1] text-[12.5px] leading-snug">{b.description}</div>
            </div>
          ))}
        </div>

        {/* Outcome — THE POINT OF THE SLIDE. Steve directive 2026-05-15:
            "the improved performance could be larger cuz thats the point
            of the slide". Now flex-1 to take the remaining vertical space,
            bigger fonts, brighter gold border. */}
        <div className="bg-[#0f1623] rounded-2xl border-2 border-[#c9a84c]/70 px-10 py-6 flex items-center gap-8 flex-1 shadow-[0_0_40px_rgba(201,168,76,0.18)]">
          <div className="text-[#c9a84c] text-[64px] font-bold tabular-nums tracking-widest leading-none">$ $ $</div>
          <div className="flex-1">
            <div className="text-white text-[36px] font-bold leading-tight mb-2">Improved performance.</div>
            <div className="text-[#cbd5e1] text-[17px] leading-snug max-w-[60ch]">
              Wider coverage, consistent scoring, faster reaction, and overnight idea generation compound into a higher-quality book, surfaced earlier, and held with more conviction. The same fundamental discipline, multiplied.
            </div>
          </div>
        </div>
      </div>
    </SlideFrame>
  );
}
